/**
 * Releases product gallery photos that were rendered "held" (see snippets/product-media.liquid,
 * hold: true) so they stop competing with the gallery's opening photo for bandwidth.
 *
 * A held <img> ships with a 1x1 placeholder src and its real src/srcset parked in
 * data-hold-src / data-hold-srcset. Everything is released at once, as soon as any of:
 *   - the opening photo (the gallery's fetchpriority="high" image) has loaded or failed,
 *   - the shopper touches, clicks, scrolls or keys inside a gallery,
 *   - a fallback timeout passes.
 * After that, any held image that appears later (a variant change re-rendering the gallery,
 * quick add, etc.) is released immediately, before it's painted.
 */

const FALLBACK_MS = 8000;
const HELD_SELECTOR = 'img[data-hold-srcset], img[data-hold-src]';

let released = false;

/** @param {HTMLImageElement} img */
function releaseImage(img) {
  const srcset = img.getAttribute('data-hold-srcset');
  const src = img.getAttribute('data-hold-src');
  img.removeAttribute('data-hold-srcset');
  img.removeAttribute('data-hold-src');
  img.removeAttribute('data-hold');
  if (srcset) img.setAttribute('srcset', srcset);
  if (src) img.setAttribute('src', src);
}

/** @param {ParentNode} root */
function releaseWithin(root) {
  root.querySelectorAll(HELD_SELECTOR).forEach((img) => releaseImage(/** @type {HTMLImageElement} */ (img)));
}

function releaseAll() {
  if (released) return;
  released = true;
  releaseWithin(document);
}

function watchForLateHeldImages() {
  new MutationObserver((mutations) => {
    if (!released) return;
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        const target = /** @type {Element} */ (mutation.target);
        if (target.matches(HELD_SELECTOR)) releaseImage(/** @type {HTMLImageElement} */ (target));
        continue;
      }
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches(HELD_SELECTOR)) releaseImage(/** @type {HTMLImageElement} */ (node));
        else releaseWithin(node);
      });
    }
  }).observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['data-hold-srcset', 'data-hold-src'],
  });
}

function init() {
  if (window.__galleryHoldInit) return;
  window.__galleryHoldInit = true;

  watchForLateHeldImages();

  const openingPhotos = /** @type {HTMLImageElement[]} */ ([
    ...document.querySelectorAll('media-gallery img[fetchpriority="high"]'),
  ]);
  if (openingPhotos.length === 0 || openingPhotos.some((img) => img.complete)) {
    releaseAll();
    return;
  }
  openingPhotos.forEach((img) => {
    img.addEventListener('load', releaseAll, { once: true });
    img.addEventListener('error', releaseAll, { once: true });
  });

  const onInteract = (/** @type {Event} */ event) => {
    if (event.target instanceof Element && event.target.closest('media-gallery, zoom-dialog')) releaseAll();
  };
  for (const type of ['pointerdown', 'touchstart', 'keydown', 'focusin']) {
    document.addEventListener(type, onInteract, { capture: true, passive: true });
  }
  document.addEventListener('scroll', onInteract, { capture: true, passive: true });

  setTimeout(releaseAll, FALLBACK_MS);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
